import { useCallback, useReducer, useRef } from 'react';
import { SharedLink } from '../services/models/shared-link';
import * as sharedLinksManager from '../services/sharedlinks';
import { useServiceErrorHandler } from './useServiceErrorHandler';

// The social feed is page-local server state (only SocialPage renders it);
// this store mirrors the retired sharedLinks Redux slice: cursor pagination
// with merge-by-id, per-item story hydration with the retry/backoff service,
// and the clicked-state patch after the storyClick mutation.

interface SharedLinksState {
  items?: SharedLink[];
  hasMore?: boolean;
  lastLoadedCursor?: number;
  isFetching?: boolean;
  isFetchingFirstPage?: boolean;
}

type SharedLinksAction =
  | { type: 'startFetching'; isFirstPage: boolean }
  | { type: 'cancelFetching' }
  | {
      type: 'completePage';
      isFirstPage: boolean;
      sharedLinks: SharedLink[];
      hasMore: boolean;
      lastCursor: number;
    }
  | { type: 'patchItem'; sharedLinkId: number; patch: Partial<SharedLink> }
  | { type: 'replaceItem'; sharedLinkId: number; sharedLink: SharedLink }
  | { type: 'patchStory'; storyId: number; patch: { isClicked: boolean } };

const mergeSharedLinks = (existing: SharedLink[], incoming: SharedLink[]): SharedLink[] => {
  const merged = existing.map(current => incoming.find(x => x.id === current.id) || current);
  incoming.forEach(link => {
    if (!existing.some(x => x.id === link.id)) {
      merged.push(link);
    }
  });
  return merged;
};

function sharedLinksReducer(state: SharedLinksState, action: SharedLinksAction): SharedLinksState {
  switch (action.type) {
    case 'startFetching':
      return { ...state, isFetching: true, isFetchingFirstPage: action.isFirstPage };
    case 'cancelFetching':
      return { ...state, isFetching: false, isFetchingFirstPage: false };
    case 'completePage':
      return {
        ...state,
        items: action.isFirstPage
          ? [...action.sharedLinks]
          : mergeSharedLinks(state.items || [], action.sharedLinks),
        hasMore: action.hasMore,
        lastLoadedCursor: action.lastCursor,
        isFetching: false,
        isFetchingFirstPage: false,
      };
    case 'patchItem':
      return {
        ...state,
        items: state.items?.map(item =>
          item.id === action.sharedLinkId ? { ...item, ...action.patch } : item
        ),
      };
    case 'replaceItem':
      return {
        ...state,
        items: state.items?.map(item =>
          item.id === action.sharedLinkId ? action.sharedLink : item
        ),
      };
    case 'patchStory':
      return {
        ...state,
        items: state.items?.map(item =>
          item.story?.id === action.storyId
            ? { ...item, story: { ...item.story, ...action.patch } }
            : item
        ),
      };
    default:
      return state;
  }
}

export const useSharedLinks = () => {
  const handleServiceError = useServiceErrorHandler();
  const [state, dispatch] = useReducer(sharedLinksReducer, {});
  // Serialize page loads the way the legacy isFetching guard did; the
  // reducer flag alone can't stop a re-entrant call issued before the
  // startFetching re-render lands.
  const fetchingPageRef = useRef(false);

  const loadSharedLinksPage = useCallback(
    async (
      after: number,
      pageSize: number | undefined,
      sortOption: string,
      timeFilter?: string,
      sharesFilter?: number
    ) => {
      if (fetchingPageRef.current) {
        return;
      }
      fetchingPageRef.current = true;
      const isFirstPage = after === 0;
      dispatch({ type: 'startFetching', isFirstPage });
      try {
        const page = await sharedLinksManager.getSharedLinksPage(
          after,
          pageSize ?? 20,
          sortOption,
          timeFilter,
          sharesFilter
        );
        dispatch({
          type: 'completePage',
          isFirstPage,
          sharedLinks: page.sharedLinks,
          hasMore: page.hasMoreSharedLinks,
          lastCursor: page.lastCursor,
        });
      } catch (error) {
        handleServiceError(error, () => dispatch({ type: 'cancelFetching' }));
      } finally {
        fetchingPageRef.current = false;
      }
    },
    [handleServiceError]
  );

  const loadSharedLink = useCallback(
    async (sharedLinkId: number) => {
      dispatch({ type: 'patchItem', sharedLinkId, patch: { isFetching: true } });
      try {
        const sharedLink = await sharedLinksManager.getSharedLink(sharedLinkId);
        dispatch({
          type: 'replaceItem',
          sharedLinkId,
          sharedLink: { ...sharedLink, isFetching: false, isLoaded: true },
        });
      } catch (error) {
        handleServiceError(error, () =>
          dispatch({ type: 'patchItem', sharedLinkId, patch: { isFetching: false } })
        );
      }
    },
    [handleServiceError]
  );

  const markStoryClicked = useCallback((storyId: number) => {
    dispatch({ type: 'patchStory', storyId, patch: { isClicked: true } });
  }, []);

  return {
    sharedLinks: state,
    loadSharedLinksPage,
    loadSharedLink,
    markStoryClicked,
  };
};
